import {
  Outlet,
  redirect,
  createRoute,
  createRouter,
  lazyRouteComponent,
  createRootRouteWithContext,
} from '@tanstack/react-router';

import type { QueryClient } from '@tanstack/react-query';

import { CONNECTORS_QUERY_KEY } from '@nexus/ari-lib/hooks/use-connectors';
import { SKILLS_QUERY_KEY } from '@nexus/ari-lib/hooks/use-skills';
import {
  AUTH_PATHS,
  LoginPage,
  SignUpPage,
  ForgotPasswordPage,
  VerifyEmailPage,
  safeRedirectPath,
} from '@nexus/supabase';

import { getDeviceActivateUrl } from '../config/global';
import { useIsAuthenticated } from '../hooks/use-is-authenticated';
import { useExplicitLogout } from '../hooks/use-explicit-logout';
import { CloudLayout } from '../layout/cloud-layout';
import { NotificationBannerHost } from '../components/notification-banner-host';
import ariCloudLogo from '../assets/ari-cloud-logo.svg';
// Dashboard is the authenticated landing route (and the LCP surface), so it
// stays eagerly imported. Every other protected route is lazy-loaded below via
// lazyRouteComponent so its code leaves the entry chunk.
import Dashboard from '../pages/dashboard';
import { fetchConnectors } from '../hooks/use-connectors';
import { requireSkillWorkflows } from '../sections/skills/skill-workflows-guard';
import { fetchSkills } from '../hooks/use-skills';

export interface RouterAuthContext {
  isAuthenticated: boolean;
}

// Router context — passed via createRouter so route loaders can prefetch
// queries against the shared cache instead of waiting for component-mount.
// `auth` is injected by RouterProvider in App so beforeLoad can gate routes.
export interface RouterContext {
  queryClient: QueryClient;
  auth: RouterAuthContext;
}

// Match the staleTime/gcTime the lib's useConnectors / useSkills hooks use,
// so ensureQueryData skips refetching when the cache is still fresh.
const CONNECTORS_STALE_TIME = 5 * 60 * 1000;
const CONNECTORS_GC_TIME = 10 * 60 * 1000;
const SKILLS_STALE_TIME = 5 * 60 * 1000;
const SKILLS_GC_TIME = 10 * 60 * 1000;

const RootComponent = () => <Outlet />;

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

// Everything that isn't a public auth route lives under here. beforeLoad reads
// the auth flag injected into router context and bounces anonymous users to
// /login (crib of apps/ari settings-redirect beforeLoad pattern).
const protectedLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  beforeLoad: ({ context, location }) => {
    if (!context.auth.isAuthenticated) {
      // Capture the requested location so login can send the user back to it.
      throw redirect({ to: AUTH_PATHS.login, search: { redirect: location.href } });
    }
  },
  // CloudLayout is the persistent shell: it mounts once here and only the <Outlet>
  // content swaps + animates on navigation. Rendering it per-page instead remounts
  // the whole chrome (incl. the heavy blurred-gradient glows) on every route
  // change, which flickers badly while a lazy chunk loads.
  component: () => (
    <>
      <CloudLayout>
        <Outlet />
      </CloudLayout>
      <NotificationBannerHost />
    </>
  ),
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/',
  component: Dashboard,
});

const documentsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/documents',
  // ?source=<documentId>.<chunkId> opens the place an answer came from (DF4). ?quote= marks only its lines.
  // ?folder=<id> and ?document=<id> open a row of Ari's folder view (DF6).
  validateSearch: (
    search: Record<string, unknown>
  ): { source?: string; quote?: string; folder?: string; document?: string } => ({
    source: typeof search.source === 'string' ? search.source : undefined,
    quote: typeof search.quote === 'string' ? search.quote : undefined,
    folder: typeof search.folder === 'string' ? search.folder : undefined,
    document: typeof search.document === 'string' ? search.document : undefined,
  }),
  component: lazyRouteComponent(() => import('../pages/documents')),
});

const chatsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/chats',
  component: lazyRouteComponent(() => import('../pages/chats')),
});

const chatDetailRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/chats/$chatId',
  component: lazyRouteComponent(() => import('../pages/chat-detail')),
});

const skillsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/skills',
  component: lazyRouteComponent(() => import('../pages/skills')),
  // Prefetch the skills catalog into the shared cache before the component
  // mounts. SkillsPageView's useSkills() then reads from cache and never
  // flashes the loading skeleton.
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData({
      queryKey: SKILLS_QUERY_KEY,
      queryFn: fetchSkills,
      staleTime: SKILLS_STALE_TIME,
      gcTime: SKILLS_GC_TIME,
    }),
});

const skillCreateRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/skills/create',
  component: lazyRouteComponent(() => import('../pages/skill-create')),
});

const skillCreateWithAriRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/skills/create-with-ari',
  component: lazyRouteComponent(() => import('../pages/skill-create-with-ari')),
});

const skillEditRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/skills/$id/edit',
  component: lazyRouteComponent(() => import('../pages/skill-edit')),
  // SkillEditView looks the skill up from the cached catalog by id, so make
  // sure it's loaded before the editor mounts (deep links / refresh).
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData({
      queryKey: SKILLS_QUERY_KEY,
      queryFn: fetchSkills,
      staleTime: SKILLS_STALE_TIME,
      gcTime: SKILLS_GC_TIME,
    }),
});

const skillWorkflowRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/skills/workflow/$id',
  beforeLoad: requireSkillWorkflows,
  component: lazyRouteComponent(() => import('../pages/skill-workflow')),
});

const skillNewRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/skills/new',
  beforeLoad: requireSkillWorkflows,
  component: lazyRouteComponent(() => import('../pages/skill-new')),
});

const skillTemplatesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/skills/browse',
  beforeLoad: requireSkillWorkflows,
  component: lazyRouteComponent(() => import('../pages/skill-templates')),
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData({
      queryKey: SKILLS_QUERY_KEY,
      queryFn: fetchSkills,
      staleTime: SKILLS_STALE_TIME,
      gcTime: SKILLS_GC_TIME,
    }),
});

const skillTemplatePreviewRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/skills/browse/$slug',
  beforeLoad: requireSkillWorkflows,
  component: lazyRouteComponent(() => import('../pages/skill-template-preview')),
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData({
      queryKey: SKILLS_QUERY_KEY,
      queryFn: fetchSkills,
      staleTime: SKILLS_STALE_TIME,
      gcTime: SKILLS_GC_TIME,
    }),
});

const connectorsRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/connectors',
  component: lazyRouteComponent(() => import('../pages/connectors')),
  // Same prefetch story as /skills above. The connection-status query
  // remains component-loaded since it's a separate query the lib hook fires.
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData({
      queryKey: CONNECTORS_QUERY_KEY,
      queryFn: fetchConnectors,
      staleTime: CONNECTORS_STALE_TIME,
      gcTime: CONNECTORS_GC_TIME,
    }),
});

const devicesRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/devices',
  component: lazyRouteComponent(() => import('../pages/devices')),
});

const mediaRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/media',
  component: lazyRouteComponent(() => import('../pages/media')),
});

const memoryRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/memory',
  component: lazyRouteComponent(() => import('../pages/memory')),
});

const accountRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/account',
  component: lazyRouteComponent(() => import('../pages/account')),
});

// Always registered so the direct /advanced URL works; only the nav tab is
// dev-gated (see cloud-layout.tsx). Mirrors how apps/ari registers its advanced
// route while gating the tab.
const advancedRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/advanced',
  component: lazyRouteComponent(() => import('../pages/advanced')),
});

// Public auth routes (the route is the mode). Login is federated-only; the
// sign-up / forgot-password / verify-email routes stay registered so existing
// links keep resolving, but nothing on the login page points at them.
// Authenticated users hitting these are redirected to the dashboard.
const cloudLogo = (
  <img src={ariCloudLogo} alt="Ari Cloud" width={178} height={32} style={{ height: 32, width: 'auto' }} />
);

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_PATHS.login,
  // Guard the post-login redirect target against open-redirect vectors.
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: safeRedirectPath(search.redirect),
  }),
  beforeLoad: ({ context, search }) => {
    // Honour the captured deep-link (?redirect=) instead of always dumping the
    // user on "/" once they authenticate.
    if (context.auth.isAuthenticated) throw redirect({ to: search.redirect ?? '/' });
  },
  component: function LoginRoute() {
    const isAuthenticated = useIsAuthenticated();
    const explicitLogout = useExplicitLogout();
    return <LoginPage isAuthenticated={isAuthenticated} explicitLogout={explicitLogout} logo={cloudLogo} />;
  },
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_PATHS.signUp,
  beforeLoad: ({ context }) => {
    if (context.auth.isAuthenticated) throw redirect({ to: '/' });
  },
  component: () => <SignUpPage logo={cloudLogo} brandingSubtitle="" />,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_PATHS.forgotPassword,
  component: () => <ForgotPasswordPage logo={cloudLogo} brandingSubtitle="" />,
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_PATHS.verifyEmail,
  // Declare ?email so the router preserves it through navigation (the page
  // reads it to prefill + enable resend).
  validateSearch: (search: Record<string, unknown>): { email?: string } => ({
    email: typeof search.email === 'string' ? search.email : undefined,
  }),
  component: () => <VerifyEmailPage logo={cloudLogo} brandingSubtitle="" />,
});

// Device code flow entry point (CLOUD-725). Its purpose is to redirect to
// the Auth0 activate page after a Auth0 logout endpoint which helps clearing cookies
const activateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/activate',
  beforeLoad: ({ preload }) => {
    if (preload) return;
    const target = getDeviceActivateUrl();
    // replace(), not assign(): Back must not bounce the user through logout again.
    if (target) window.location.replace(target);
  },
  component: lazyRouteComponent(() => import('../pages/activate')),
});

// Subscription upgrade page — authenticated, so it lives under the protected layout.
const upgradeRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/upgrade',
  component: lazyRouteComponent(() => import('../pages/upgrade')),
});

// Stripe Checkout redirect landing pages — authenticated, so they live under
// the protected layout alongside /upgrade.
const upgradeSuccessRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/upgrade/success',
  component: lazyRouteComponent(() => import('../pages/upgrade-success')),
});

const upgradeCancelRoute = createRoute({
  getParentRoute: () => protectedLayoutRoute,
  path: '/upgrade/cancel',
  component: lazyRouteComponent(() => import('../pages/upgrade-cancel')),
});

const routeTree = rootRoute.addChildren([
  protectedLayoutRoute.addChildren([
    dashboardRoute,
    documentsRoute,
    chatsRoute,
    chatDetailRoute,
    skillsRoute,
    skillCreateRoute,
    skillCreateWithAriRoute,
    skillEditRoute,
    skillWorkflowRoute,
    skillNewRoute,
    skillTemplatesRoute,
    skillTemplatePreviewRoute,
    connectorsRoute,
    devicesRoute,
    mediaRoute,
    memoryRoute,
    accountRoute,
    advancedRoute,
    upgradeRoute,
    upgradeSuccessRoute,
    upgradeCancelRoute,
  ]),
  loginRoute,
  signUpRoute,
  forgotPasswordRoute,
  verifyEmailRoute,
  activateRoute,
]);

/**
 * Create the ari-cloud router. queryClient powers loader prefetch; `auth` is a
 * placeholder here and is supplied for real by RouterProvider's `context` prop
 * in App.
 */
export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    defaultPreload: 'intent',
    context: { queryClient, auth: { isAuthenticated: false } },
  });
}

// Register the router type for type-safe navigate() / useParams() / etc.
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
